import React from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@/components/Common/Button';
import Dropdown, { FieldInfo } from '@/components/Common/Dropdown';
import FlexRow from '@/components/Common/FlexRow';
import Input from '@/components/Common/Input';

import { CatHeader } from '@/pages/Projects/Project';

export interface AuditSearchParams {
    start_time?: string;
    end_time?: string;
    user?: string;
    type?: string;
    operation?: string;
    result?: string;
    id?: string;
    secondary_id?: string;
    search?: string;
    limit?: number;
}

export interface AuditSearchFormState {
    startTime: string;
    endTime: string;
    user: string;
    type: string;
    operation: string;
    result: string;
    id: string;
    secondaryId: string;
    search: string;
    limit: string;
}

export const initialSearchState: AuditSearchFormState = {
    startTime: "",
    endTime: "",
    user: "",
    type: "",
    operation: "",
    result: "",
    id: "",
    secondaryId: "",
    search: "",
    limit: "5000",
};

interface AuditSearchProps {
    onSearch: (params: AuditSearchParams) => void;
    onCancel: () => void;
    formState: AuditSearchFormState;
    setFormState: React.Dispatch<React.SetStateAction<AuditSearchFormState>>;
}

const AuditSearch = ({ onSearch, onCancel, formState, setFormState }: AuditSearchProps) => {
    const { t } = useTranslation();

    const typeOpts: FieldInfo[] = [
        { value: "", desc: "--" },
        { value: "project", desc: t("audit:type_options:project") },
        { value: "limits", desc: t("audit:type_options:limits") },
        { value: "room", desc: t("audit:type_options:room") },
        { value: "user", desc: t("audit:type_options:user") },
        { value: "channel", desc: t("audit:type_options:channel") },
        { value: "global_channel_list", desc: t("audit:type_options:global_channel_list") },
        { value: "local_channel_list", desc: t("audit:type_options:local_channel_list") },
        { value: "login", desc: t("audit:type_options:login") },
        { value: "language", desc: t("audit:type_options:language") },
        { value: "tag", desc: t("audit:type_options:tag") },
        { value: "app", desc: t("audit:type_options:app") },
        { value: "secondary_id", desc: t("audit:type_options:secondary_id") },
    ];

    const operationOpts: FieldInfo[] = [
        { value: "", desc: "--" },
        { value: "update", desc: t("audit:operation_options:update") },
        { value: "delete", desc: t("audit:operation_options:delete") },
        { value: "append", desc: t("audit:operation_options:create") },
    ];

    const resultOpts: FieldInfo[] = [
        { value: "", desc: "--" },
        { value: "success", desc: t("audit:result_options:success") },
        { value: "failure", desc: t("audit:result_options:failure") },
    ];

    const updateField = (field: keyof AuditSearchFormState) => (value: string) => {
        setFormState(prev => ({ ...prev, [field]: value }));
    };

    const handleSearch = () => {
        const params: AuditSearchParams = {};

        // Convert datetime-local to Unix timestamp (milliseconds)
        if (formState.startTime) {
            const startDate = new Date(formState.startTime);
            if (!isNaN(startDate.getTime())) {
                params.start_time = startDate.getTime().toString();
            }
        }
        if (formState.endTime) {
            const endDate = new Date(formState.endTime);
            if (!isNaN(endDate.getTime())) {
                params.end_time = endDate.getTime().toString();
            }
        }
        if (formState.user) params.user = formState.user;
        if (formState.type) params.type = formState.type;
        if (formState.operation) params.operation = formState.operation;
        if (formState.result) params.result = formState.result;
        if (formState.id) params.id = formState.id;
        if (formState.secondaryId) params.secondary_id = formState.secondaryId;
        if (formState.search) params.search = formState.search;
        if (formState.limit) {
            const limitNum = parseInt(formState.limit);
            if (!isNaN(limitNum) && limitNum > 0) {
                params.limit = limitNum;
            }
        }

        onSearch(params);
    };

    const handleClear = () => {
        setFormState(initialSearchState);
    };

    return (
        <React.Fragment>
            <FlexRow direction="column"  
                     align="start"
                     className="px-[32px] pb-[120px]">
                <FlexRow direction="column"
                         align="start"
                         className="py-0 gap-[16px] max-w-[620px]">
                    <CatHeader caption='audit:search_title' />

                    <Input label={t('audit:start_time')}
                           placeholder={t('audit:start_time_placeholder')}
                           type="datetime-local"
                           value={formState.startTime}
                           onValueChange={updateField('startTime')} />
   
                    <Input label={t('audit:end_time')}
                           placeholder={t('audit:end_time_placeholder')}
                           type="datetime-local"
                           value={formState.endTime}
                           onValueChange={updateField('endTime')} />

                    <Input label={t('audit:filter_user')}
                           placeholder={t('audit:filter_user_placeholder')}
                           value={formState.user}
                           onValueChange={updateField('user')} />

                    <Dropdown label={t('audit:filter_type')}
                              value={formState.type}
                              placeholder="--"
                              options={typeOpts}
                              setValue={updateField('type')}>
                        <></>
                    </Dropdown>

                    <Dropdown label={t('audit:filter_operation')}
                              value={formState.operation}
                              placeholder="--"
                              options={operationOpts}
                              setValue={updateField('operation')}>
                        <></>
                    </Dropdown>

                    <Dropdown label={t('audit:filter_result')}
                              value={formState.result}
                              placeholder="--"
                              options={resultOpts}
                              setValue={updateField('result')}>
                        <></>
                    </Dropdown>

                    <Input label={t('audit:filter_id')}
                           placeholder={t('audit:filter_id_placeholder')}
                           value={formState.id}
                           onValueChange={updateField('id')} />

                    <Input label={t('audit:filter_secondary_id')}
                           placeholder={t('audit:filter_secondary_id_placeholder')}
                           value={formState.secondaryId}
                           onValueChange={updateField('secondaryId')} />

                    <Input label={t('audit:filter_search')}
                           placeholder={t('audit:filter_search_placeholder')}
                           value={formState.search}
                           onValueChange={updateField('search')} />

                    <Input label={t('audit:filter_limit')}
                           placeholder="5000"
                           type="number"
                           value={formState.limit}
                           onValueChange={updateField('limit')} />
                </FlexRow>
            </FlexRow>
            <FlexRow className="absolute bottom-0 p-[32px] bg-white z-10 justify-end gap-[16px]">
                <Button className="uppercase"
                        caption={t('audit:clear')}
                        onClick={handleClear}
                        layout="white" />
                <Button className="uppercase"
                        caption={t('audit:cancel')}
                        onClick={onCancel}
                        layout="white" />
                <Button className="uppercase"
                        caption={t('audit:apply')}
                        onClick={handleSearch} />
            </FlexRow>
        </React.Fragment>
    );
};

export default AuditSearch;
